import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicDocumentApi, type DirectPurchaseOrderResponse, type PublicBusinessContactInfo } from '@/lib/api';
import { transformLineItems, calculateSubtotal } from '@/components/TemplateDesigner/utils';
import { DocumentRenderer, buildOwlOpsPayload } from '@/components/TemplateDesigner/DocumentRenderer';
import type { TemplateSettings, DocumentData, BusinessData } from '@/components/TemplateDesigner/types';
import { DocumentNotFound } from './DocumentNotFound';
import { FullDocumentView } from './FullDocumentView';
import { Loader2 } from 'lucide-react';

export function PublicPurchaseOrder() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DirectPurchaseOrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFoundBusiness, setNotFoundBusiness] = useState<PublicBusinessContactInfo | null>(null);

  // Prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!id) return;

    const loadPO = async () => {
      const response = await publicDocumentApi.getPurchaseOrder(id);

      if (response.error) {
        // Try to extract business info from error response for not found page
        try {
          const errorData = JSON.parse(response.error);
          if (errorData.business) {
            setNotFoundBusiness(errorData.business);
          }
        } catch {
          // Ignore parse errors
        }
        setLoading(false);
        return;
      }

      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };

    loadPO();
  }, [id]);

  // Get template settings from template_config (must be before early returns)
  const templateSettings = useMemo(() => {
    if (!data?.template_config?.settings) return undefined;
    return data.template_config.settings as unknown as TemplateSettings;
  }, [data]);

  // Build document data for renderer (must be before early returns)
  const documentData: DocumentData | null = useMemo(() => {
    if (!data) return null;

    const { purchase_order, vendor, taxes } = data;

    // Use computed values (markup baked into line items)
    const computedSubtotal = calculateSubtotal(purchase_order.line_items);

    return {
      documentType: 'purchase_order' as const,
      id: purchase_order.id,
      purchase_order_number: purchase_order.purchase_order_number,
      issued_date: purchase_order.issued_date || '',
      status: purchase_order.status,
      vendor_name: vendor?.billing_name || '',
      vendor_company: vendor?.company_name || '',
      vendor_address: vendor?.address || '',
      note_for_vendor: purchase_order.note_for_vendor || '',
      line_items: transformLineItems(purchase_order.line_items, taxes || {}),
      subtotal: computedSubtotal,
      total_markup: 0,  // Markup is baked into line item rates, don't show separately
      total_amount: parseFloat(purchase_order.total_amount),
      // Custom fields
      custom_field_values: purchase_order.custom_field_values,
      // Shipping data
      ship_enabled: purchase_order.ship_enabled,
      ship_fee: purchase_order.ship_fee,
      ship_date: purchase_order.ship_date,
      ship_via: purchase_order.ship_via,
      ship_tracking_number: purchase_order.ship_tracking_number,
    };
  }, [data]);

  // Build business data for renderer (must be before early returns)
  const businessData: BusinessData = useMemo(() => {
    if (!data) return { name: '', address: '', phone: '', email: '', logo: '' };
    const { business } = data;
    return {
      name: business.name,
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      logo: business.logo || '',
    };
  }, [data]);

  // On-demand PDF generation callback (must be before early returns)
  const handleDownloadPdf = useCallback(async () => {
    if (!templateSettings || !documentData) {
      throw new Error('Template settings not available');
    }
    const payload = buildOwlOpsPayload(documentData, businessData, templateSettings);
    return publicDocumentApi.renderPdf({
      template_slug: templateSettings.selectedLayout,
      data: payload.data,
      settings: payload.settings,
    });
  }, [documentData, businessData, templateSettings]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return <DocumentNotFound documentType="Purchase Order" business={notFoundBusiness} />;
  }

  const { purchase_order, business } = data;

  // Build document content using DocumentRenderer
  const documentContent = templateSettings && documentData ? (
    <DocumentRenderer
      isPublic
      data={documentData}
      settings={templateSettings}
      business={businessData}
    />
  ) : (
    <div className="p-8 text-center text-gray-500">
      Unable to render document. Please try again later.
    </div>
  );

  return (
    <FullDocumentView
      documentType="purchase_order"
      business={business}
      themeColor={templateSettings?.colorTheme?.primary}
      logoUrl={templateSettings?.logo?.url}
      showCompanyName={templateSettings?.logo?.showCompanyName ?? true}
      status={purchase_order.status}
      statusLabel="Sent"
      pdfUrl={purchase_order.pdf_url}
      onDownloadPdf={templateSettings && documentData ? handleDownloadPdf : undefined}
      pdfFilename={`PurchaseOrder-${purchase_order.purchase_order_number}.pdf`}
    >
      {documentContent}
    </FullDocumentView>
  );
}
